import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import { UploadedCsvFile, UploadedFile } from '../@types/uploaded-files.type';
import {
  exportDispatchManifest,
  exportFiles,
  uploadFiles,
  getUploadedCsvFile,
  getUploadedFilePresignedUrlById,
  getUploadedFiles,
  updateUploadedFileData,
  getUploadedFilePresignedUrlByKey,
  ExportResponse,
  ExportInfo,
  UploadFilesResponse,
  UploadedCsvFileResponse,
  ManifestExportResponse,
} from '../api/uploadedFiles';
import { ConfigurationHistoriesKey } from '../configs/part-type';
import {
  ExportFilesKey,
  UploadedFilesKey,
  UploadedImagesKey,
  UploadMultipleImagesKey,
} from '../configs/uploaded-files';

// Upload files mutation data
interface UploadFilesData {
  entityId: number;
  formData: FormData;
  entityType: string;
}

interface UploadFileAditionalData {
  id: number;
  isMain: boolean;
}

export const useExportFile = (): UseMutationResult<ExportResponse, Error, ExportInfo> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exportInfo: ExportInfo) => {
      const response = await exportFiles(exportInfo);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ExportFilesKey],
      });
    },
  });
};

export const useGetUploadedFiles = (
  entityId: number,
  entityType: string,
  notIncludeUrlsByDefault?: boolean
): UseQueryResult<UploadedFile[]> => {
  return useQuery({
    queryKey: [UploadedFilesKey, entityId, entityType],
    queryFn: ({ signal }) =>
      getUploadedFiles(entityId, entityType, notIncludeUrlsByDefault, signal),
    enabled: !!entityId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useGetUploadedImages = (
  entityId: number,
  entityType: string
): UseQueryResult<UploadedFile[]> => {
  return useQuery({
    queryKey: [UploadMultipleImagesKey, entityId, entityType],
    queryFn: ({ signal }) => getUploadedFiles(entityId, entityType, undefined, signal),
    enabled: entityId !== -1,
  });
};

export const useUpdateUploadedFileAditionalData = (
  entityId: number
): UseMutationResult<void, Error, UploadFileAditionalData> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UploadFileAditionalData) => {
      await updateUploadedFileData(data.id, data.isMain);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [UploadedImagesKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, entityId],
      });
    },
  });
};

export const useGetUploadedCsvFile = (key: string): UseQueryResult<UploadedCsvFileResponse> => {
  return useQuery({
    queryKey: [UploadedFilesKey, key],
    queryFn: ({ signal }) => getUploadedCsvFile(key, signal),
    enabled: false,
  });
};

export const useGetUploadedFilePresignedUrlById = (
  id: number,
  contentDispositionType?: string
): UseQueryResult<UploadedCsvFile> => {
  return useQuery({
    queryKey: [UploadedFilesKey, id, contentDispositionType],
    queryFn: ({ signal }) => getUploadedFilePresignedUrlById(id, contentDispositionType, signal),
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

export const useGetUploadedFilePresignedUrlByKey = (
  key: string,
  mimeType?: string,
  contentDispositionType?: string
): UseQueryResult<UploadedCsvFile> => {
  return useQuery({
    queryKey: [UploadedFilesKey, key, mimeType, contentDispositionType],
    queryFn: ({ signal }) =>
      getUploadedFilePresignedUrlByKey(key, mimeType, contentDispositionType, signal),
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

export const useExportDispatchManifest = (): UseMutationResult<
  ManifestExportResponse,
  Error,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shipmentId: number) => {
      const response = await exportDispatchManifest(shipmentId);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ExportFilesKey],
      });
    },
  });
};

export const useUploadFiles = (
  entityId: number,
  entityType: string
): UseMutationResult<UploadFilesResponse, Error, UploadFilesData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UploadFilesData) => {
      return await uploadFiles(data.entityId, data.formData, data.entityType);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ConfigurationHistoriesKey, entityId],
      });
      await queryClient.invalidateQueries({
        queryKey: [UploadMultipleImagesKey, entityId, entityType],
      });
    },
  });
};
